import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  PerformancePage,
  PerformancePageOverview,
} from 'api/new-analytics/types';
import GraphCard from 'app/modules/analytics/components/shared/GraphCard';
import {
  BarChart,
  ChartContent,
  LineChart,
} from 'app/modules/analytics/components/shared/charts';
import {
  REPORT_PREVIEW_CHART_CARD_CLASS_NAME,
  REPORT_PREVIEW_CHART_HEIGHT_PX,
  REPORT_PREVIEW_CHART_PLACEHOLDER_CLASS_NAME,
} from 'app/modules/analytics/modules/reports/constants/reportPreviewChartLayout';
import {
  buildMultiSeriesByPlatform,
  buildStackedBarDatasetsByPlatform,
} from 'app/modules/analytics/modules/performance/utils/buildGroupPerformanceChartData';
import { getMetricDisplay } from 'app/modules/analytics/modules/performance/utils/getPerformanceMetricDisplay';
import { useReportPreviewRenderReadyBlock } from 'app/modules/analytics/modules/reports/context/ReportPreviewRenderReadyContext';
import { getPerformanceChartConfig } from 'app/modules/analytics/modules/reports/utils/getPerformanceChartConfig';

interface Props {
  metricKey: string;
  pages: PerformancePage[];
  overview: PerformancePageOverview | undefined;
  isLoading: boolean;
}

const ReportPreviewMetricChart = ({
  metricKey,
  pages,
  overview,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const instanceId = useId();
  const config = getPerformanceChartConfig(metricKey);

  const chartData = useMemo(() => {
    if (!config) {
      return { labels: [], datasets: [] };
    }
    if (config.chartType === 'bar') {
      return buildStackedBarDatasetsByPlatform(pages, metricKey);
    }
    return buildMultiSeriesByPlatform(pages, metricKey);
  }, [config, metricKey, pages]);

  const isEmpty = chartData.labels.length === 0;
  const blockId = `${instanceId}-${metricKey}`;
  const { isTracking, markReady } = useReportPreviewRenderReadyBlock(blockId, {
    enabled: !!config,
    immediateReady: isLoading || isEmpty,
  });

  const legend = useMemo(
    () =>
      chartData.datasets.map((dataset) => ({
        label: dataset.label,
        color: dataset.color,
        platform: dataset.platform,
        id: dataset.platform,
      })),
    [chartData.datasets],
  );

  const metricDisplay = getMetricDisplay(overview, metricKey);

  if (!config) {
    return null;
  }

  const chartRenderProps = isTracking
    ? {
        disableAnimation: true,
        onRendered: markReady,
      }
    : {};

  return (
    <GraphCard
      title={t(config.titleKey)}
      value={metricDisplay.value}
      badge={metricDisplay.badge}
      legend={legend}
      className={REPORT_PREVIEW_CHART_CARD_CLASS_NAME}
    >
      <ChartContent
        isLoading={isLoading}
        isEmpty={isEmpty}
        emptyMessage={t('analyticsPerformance.noDataAvailable')}
        placeholderClassName={REPORT_PREVIEW_CHART_PLACEHOLDER_CLASS_NAME}
      >
        {config.chartType === 'bar' ? (
          <BarChart
            labels={chartData.labels}
            tooltipTitles={chartData.tooltipTitles}
            datasets={chartData.datasets}
            stacked={config.stacked}
            height={REPORT_PREVIEW_CHART_HEIGHT_PX}
            {...chartRenderProps}
          />
        ) : (
          <LineChart
            labels={chartData.labels}
            tooltipTitles={chartData.tooltipTitles}
            datasets={chartData.datasets}
            height={REPORT_PREVIEW_CHART_HEIGHT_PX}
            {...chartRenderProps}
          />
        )}
      </ChartContent>
    </GraphCard>
  );
};

export default ReportPreviewMetricChart;
